import React, { useEffect, useRef } from "react";
import { DialogTrigger } from "react-aria-components";
import { MDSIcon } from "@/mds/components/icon";
import {
  MDSSelectMenu,
  MDSSelectMenuItem,
  MDSMenuDropdownPopover,
  MDSMenuItemDivider,
  MDSMenuItemHeader,
} from "@/mds/components/menu";
import {
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
} from "@/mds/components/menu/menu-item/menu-item.types";
import { MDSMenuItemHeaderVariant } from "@/mds/components/menu/menu-item-header/menu-item-header.types";
import { MDSBadge, MDSBadgeVariant } from "@/mds/components/badge";
import { MDSComboboxDropdownConfig } from "./combobox.types";
import { MDSButton, MDSButtonSlotName, MDSButtonVariant } from "../../buttons";

export interface ComboboxDropdownProps {
  config: MDSComboboxDropdownConfig;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSelect: (optionId: string) => void;
  buttonVariant?: MDSButtonVariant;
  isLoading?: boolean;
}

export const ComboboxDropdown = ({
  config,
  isOpen,
  onOpenChange,
  onSelect,
  buttonVariant = MDSButtonVariant.Transparent,
  isLoading,
}: ComboboxDropdownProps) => {
  const selectedOption = config.options.find(opt => opt.id === config.selectedId);
  const defaultOptionId = config.defaultOptionId ?? config.options[0]?.id;
  const dropdownWidthPx = config.dropdownWidthPx ?? 176;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const isNonModal = config.dropdownMode === "implicit";

  // Manual outside-click and Escape dismissal for non-modal popovers.
  // React Aria's non-modal Popover skips outside-click detection, so we
  // handle it ourselves to support Shadow DOM environments.
  useEffect(() => {
    if (!isOpen || !isNonModal) return;

    const handlePointerDown = (e: Event) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      onOpenChange(false);
    };

    const handleKeyDown = (e: Event) => {
      if ((e as KeyboardEvent).key === "Escape") {
        onOpenChange(false);
      }
    };

    // Listen on the root node (shadow root or document) so events inside
    // the Shadow DOM are captured.
    const root = triggerRef.current?.getRootNode() ?? document;
    root.addEventListener("pointerdown", handlePointerDown, true);
    root.addEventListener("keydown", handleKeyDown, true);

    return () => {
      root.removeEventListener("pointerdown", handlePointerDown, true);
      root.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen, isNonModal, onOpenChange]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <MDSButton
        ref={triggerRef}
        data-test-id="combobox-dropdown-trigger"
        variant={buttonVariant}
        isLoading={isLoading}
        style={{ maxHeight: 32 }}
      >
        {/* Only show icon when a non-default option is selected */}
        {selectedOption && selectedOption.id !== defaultOptionId && selectedOption.icon && (
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={selectedOption.icon} size={14} />
        )}
        {selectedOption?.label ?? config.options[0]?.label}
      </MDSButton>
      <MDSMenuDropdownPopover
        ref={popoverRef}
        placement="top end"
        isNonModal={isNonModal}
        style={{ width: dropdownWidthPx }}
      >
        <MDSSelectMenu
          aria-label="Select option"
          selectionMode="single"
          selectedKeys={new Set([config.selectedId])}
          onSelectionChange={keys => {
            const selected = keys as Set<string>;
            const selectedId = selected.values().next().value;
            if (selectedId) {
              onSelect(selectedId);
            }
            // Close the popover after any selection interaction (including re-selecting the current item)
            onOpenChange(false);
          }}
        >
          {config.options.map((option, index) => {
            const isSelected = option.id === config.selectedId;
            const showDivider =
              config.dividerAfterIndex !== undefined && index === config.dividerAfterIndex + 1;
            const showSectionHeader = config.sectionHeader && index === 1;

            // Determine the right addon based on option config
            const getRightAddon = () => {
              if (isSelected) return undefined;
              if (option.showBadge) {
                return () => (
                  <MDSBadge label={option.badgeLabel ?? "Pro"} variant={MDSBadgeVariant.Default} />
                );
              }
              return undefined;
            };

            return (
              <React.Fragment key={option.id}>
                {showDivider && <MDSMenuItemDivider />}
                {showSectionHeader && (
                  <>
                    <MDSMenuItemDivider />
                    <MDSMenuItemHeader
                      label={config.sectionHeader!}
                      variant={MDSMenuItemHeaderVariant.Beginning}
                    />
                  </>
                )}
                <MDSSelectMenuItem
                  id={option.id}
                  label={option.label}
                  icon={option.icon ? () => <MDSIcon kind={option.icon!} size={16} /> : undefined}
                  isSelected={isSelected}
                  selectionVariant={isSelected ? MDSMenuItemSelectionVariant.Checkmark : undefined}
                  rightAddon={getRightAddon()}
                  variant={MDSMenuItemVariant.OneLine}
                />
              </React.Fragment>
            );
          })}
          {config.footer && <MDSMenuItemDivider />}
        </MDSSelectMenu>
        {config.footer}
      </MDSMenuDropdownPopover>
    </DialogTrigger>
  );
};
